import { useState, useRef } from "react";
import axios from "axios";
import ListadoDocumentos from "../components/ListadoDocumentos";
import ListadoVideos from "../components/ListadoVideos";

const API_URL = "https://api.odonto-bot.cl";
const MAX_VIDEO_SIZE_MB = 35;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

export default function AdminPanel() {
  const usuarioLogeado = localStorage.getItem("usuario") || "Administrador";

  const [tabActiva, setTabActiva] = useState("documentos");

  const [archivo, setArchivo] = useState(null);
  const [video, setVideo] = useState(null);

  const [mensaje, setMensaje] = useState("");
  const [mensajeVideo, setMensajeVideo] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingVideo, setLoadingVideo] = useState(false);

  const [progreso, setProgreso] = useState(0);
  const [progresoVideo, setProgresoVideo] = useState(0);

  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // 🔐 LOGOUT
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    window.location.href = "/login";
  };

  // =========================
  // 📄 SUBIR PDF
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje("");
    setProgreso(0);

    if (!archivo) {
      setMensaje("Debes seleccionar un archivo PDF");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setMensaje("Sesión expirada. Inicia sesión nuevamente.");
      return;
    }

    const formData = new FormData();
    formData.append("archivo", archivo);

    try {
      setLoading(true);

      await axios.post(`${API_URL}/documentos/subir`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (e) => {
          const porcentaje = Math.round((e.loaded * 100) / e.total);
          setProgreso(porcentaje);
        },
      });

      setMensaje("Documento subido correctamente ✔");
      setArchivo(null);
      setTimeout(() => window.location.reload(), 900);
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || "Error al subir el documento");
    } finally {
      setLoading(false);
      setTimeout(() => setProgreso(0), 1200);
    }
  };

  // =========================
  // 🎥 VALIDAR VIDEO
  // =========================
  const handleVideoChange = (e) => {
    const archivoSeleccionado = e.target.files?.[0];

    setMensajeVideo("");
    setProgresoVideo(0);

    if (!archivoSeleccionado) {
      setVideo(null);
      return;
    }

    if (archivoSeleccionado.size > MAX_VIDEO_SIZE_BYTES) {
      setVideo(null);

      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }

      setMensajeVideo(
        `El video supera el tamaño máximo permitido de ${MAX_VIDEO_SIZE_MB} MB. Comprímelo antes de subirlo.`
      );
      return;
    }

    setVideo(archivoSeleccionado);
  };

  // =========================
  // 🎥 SUBIR VIDEO
  // =========================
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    setMensajeVideo("");
    setProgresoVideo(0);

    if (!video) {
      setMensajeVideo("Debes seleccionar un video");
      return;
    }

    if (video.size > MAX_VIDEO_SIZE_BYTES) {
      setMensajeVideo(
        `El video supera el tamaño máximo permitido de ${MAX_VIDEO_SIZE_MB} MB. Comprímelo antes de subirlo.`
      );
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setMensajeVideo("Sesión expirada. Inicia sesión nuevamente.");
      return;
    }

    const formData = new FormData();
    formData.append("video", video);

    try {
      setLoadingVideo(true);
      setMensajeVideo("Subiendo video...");

      await axios.post(`${API_URL}/videos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const porcentaje = Math.round((e.loaded * 100) / e.total);
          setProgresoVideo(porcentaje);

          if (porcentaje >= 100) {
            setMensajeVideo("Procesando video...");
          }
        },
      });

      setMensajeVideo("Video listo ✅");
      setVideo(null);

      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }

      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      setMensajeVideo(error.response?.data?.error || "Error al subir el video");
    } finally {
      setLoadingVideo(false);
      setTimeout(() => setProgresoVideo(0), 1200);
    }
  };

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Odonto.bot</h1>
          <p style={styles.subtitle}>
            Panel administrativo · Universidad de Chile
          </p>
        </div>

        <div style={styles.headerActions}>
          <div style={styles.userBadge}>
            👤 Bienvenido, <strong>{usuarioLogeado}</strong>
          </div>

          <button onClick={handleLogout} style={styles.logoutBtn}>
            Cerrar sesión
          </button>
        </div>
      </header>

      <div style={styles.container}>
        <p style={styles.context}>
          Desde este panel puedes gestionar los documentos oficiales y videos que
          alimentan el conocimiento del asistente Odonto.bot.
        </p>

        {/* TABS */}
        <div style={styles.tabsWrapper}>
          <button
            type="button"
            onClick={() => setTabActiva("documentos")}
            style={{
              ...styles.tabBtn,
              ...(tabActiva === "documentos" ? styles.tabBtnActive : {}),
            }}
          >
            Documentos PDF
          </button>

          <button
            type="button"
            onClick={() => setTabActiva("videos")}
            style={{
              ...styles.tabBtn,
              ...(tabActiva === "videos" ? styles.tabBtnActive : {}),
            }}
          >
            Videos
          </button>
        </div>

        {/* TAB DOCUMENTOS */}
        {tabActiva === "documentos" && (
          <>
            <section style={styles.primaryCard}>
              <h3 style={styles.cardTitle}>Agregar documento PDF</h3>

              <form onSubmit={handleSubmit}>
                <div style={styles.fileBox}>
                  <input
                    type="file"
                    accept="application/pdf"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={(e) => setArchivo(e.target.files[0])}
                  />

                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => fileInputRef.current.click()}
                  >
                    Seleccionar PDF
                  </button>

                  <span style={styles.fileName}>
                    {archivo ? archivo.name : "Ningún archivo seleccionado"}
                  </span>
                </div>

                <button style={styles.primaryBtn} disabled={loading}>
                  {loading ? "Subiendo..." : "Subir documento"}
                </button>
              </form>

              {loading && <p>{progreso}%</p>}
              {mensaje && <p style={styles.message}>{mensaje}</p>}
            </section>

            <section style={styles.secondaryCard}>
              <h3 style={styles.cardTitle}>Documentos cargados</h3>
              <ListadoDocumentos />
            </section>
          </>
        )}

        {/* TAB VIDEOS */}
        {tabActiva === "videos" && (
          <>
            <section style={styles.primaryCard}>
              <h3 style={styles.cardTitle}>Agregar video</h3>

              <p style={styles.videoRule}>
                Tamaño máximo permitido: <strong>{MAX_VIDEO_SIZE_MB} MB</strong>.
                Para obtener mejores tiempos de carga, se recomienda subir videos comprimidos.
              </p>

              <form onSubmit={handleVideoSubmit}>
                <div style={styles.fileBox}>
                  <input
                    type="file"
                    accept="video/*"
                    ref={videoInputRef}
                    style={{ display: "none" }}
                    onChange={handleVideoChange}
                  />

                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => videoInputRef.current.click()}
                    disabled={loadingVideo}
                  >
                    Seleccionar video
                  </button>

                  <span style={styles.fileName}>
                    {video ? video.name : "Ningún video seleccionado"}
                  </span>
                </div>

                <button style={styles.primaryBtn} disabled={loadingVideo || !video}>
                  {loadingVideo ? "Subiendo video..." : "Subir video"}
                </button>
              </form>

              {loadingVideo && (
                <p style={styles.progressText}>
                  {mensajeVideo} {progresoVideo > 0 && progresoVideo < 100 ? `${progresoVideo}%` : ""}
                </p>
              )}

              {!loadingVideo && mensajeVideo && (
                <p style={styles.message}>{mensajeVideo}</p>
              )}
            </section>

            <section style={styles.secondaryCard}>
              <h3 style={styles.cardTitle}>Videos cargados</h3>
              <ListadoVideos />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/* =========================
   🎨 ESTILOS
========================= */

const styles = {
  page: {
    backgroundColor: "#EEF1F5",
    minHeight: "100vh",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#1F2937",
  },

  header: {
    backgroundColor: "#0033A0",
    padding: "22px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#FFFFFF",
    gap: 20,
  },

  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 600,
  },

  subtitle: {
    margin: 0,
    fontSize: 14,
    opacity: 0.9,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },

  userBadge: {
    backgroundColor: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#FFFFFF",
    padding: "9px 14px",
    borderRadius: 999,
    fontSize: 14,
    whiteSpace: "nowrap",
  },

  logoutBtn: {
    backgroundColor: "rgba(255,255,255,0.15)",
    color: "#FFFFFF",
    border: "1px solid rgba(255,255,255,0.3)",
    padding: "10px 18px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14,
    outline: "none",
    whiteSpace: "nowrap",
  },

  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: 24,
  },

  context: {
    fontSize: 15,
    color: "#374151",
    marginBottom: 22,
  },

  tabsWrapper: {
    display: "flex",
    gap: 10,
    marginBottom: 20,
    backgroundColor: "#FFFFFF",
    padding: 8,
    borderRadius: 12,
    width: "fit-content",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
  },

  tabBtn: {
    border: "none",
    backgroundColor: "transparent",
    color: "#374151",
    padding: "10px 18px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },

  tabBtnActive: {
    backgroundColor: "#0033A0",
    color: "#FFFFFF",
  },

  primaryCard: {
    backgroundColor: "#fff",
    padding: 24,
    borderRadius: 12,
    marginBottom: 20,
  },

  secondaryCard: {
    backgroundColor: "#fff",
    padding: 24,
    borderRadius: 12,
  },

  cardTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 12,
  },

  videoRule: {
    backgroundColor: "#EFF6FF",
    border: "1px solid #BFDBFE",
    color: "#1E3A8A",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 14,
    marginTop: 0,
    marginBottom: 14,
  },

  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },

  fileName: {
    fontSize: 14,
  },

  primaryBtn: {
    backgroundColor: "#0033A0",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
  },

  secondaryBtn: {
    backgroundColor: "#eee",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
    border: "1px solid #D1D5DB",
  },

  message: {
    marginTop: 10,
  },

  progressText: {
    marginTop: 10,
    fontSize: 14,
    color: "#374151",
    fontWeight: 500,
  },
};